import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [formData, setFormData] = useState({
    projectType: '',
    zipCode: '',
    timeline: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [showQuotePopup, setShowQuotePopup] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowQuotePopup(true);
    }, 20000);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d5sg05j99ei0h7kmai10', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          projectType: '',
          zipCode: '',
          timeline: '',
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src="https://public.readdy.ai/ai/img_res/75f433143b37aee76df13d54557b84f3.jpg" 
                alt="MT CRUZ Logo" 
                className="h-16 sm:h-20 w-auto object-contain"
              />
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#technical-edge" className="text-gray-700 hover:text-orange-700 transition-colors text-sm font-medium whitespace-nowrap">Technical Edge</a>
              <a href="#projects" className="text-gray-700 hover:text-orange-700 transition-colors text-sm font-medium whitespace-nowrap">Featured Projects</a>
              <a href="#mission" className="text-gray-700 hover:text-orange-700 transition-colors text-sm font-medium whitespace-nowrap">Our Mission</a>
              <a href="#gnx" className="text-gray-700 hover:text-orange-700 transition-colors text-sm font-medium whitespace-nowrap">About GNX</a>
              <a href="#quote" className="bg-orange-700 text-white px-10 py-4 rounded-xl hover:bg-orange-600 transition-all text-lg font-bold whitespace-nowrap shadow-2xl hover:shadow-3xl transform hover:scale-110">
                Request GA Site Assessment
              </a>
            </div>

            <button className="md:hidden text-gray-700 cursor-pointer">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Quote Popup */}
      {showQuotePopup && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform animate-scaleIn">
            <div className="relative p-6 sm:p-8">
              <button 
                onClick={() => setShowQuotePopup(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-700"></i>
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-list-3-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                  Ready to Master Your Georgia Land?
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Get a detailed technical assessment within 3 hours. Licensed, bonded, and fully insured.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-orange-700 text-lg"></i>
                  </div>
                  <span className="text-sm">Response within 3 hours</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-orange-700 text-lg"></i>
                  </div>
                  <span className="text-sm">Red clay precision grading</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-orange-700 text-lg"></i>
                  </div>
                  <span className="text-sm">Professional service guarantee</span>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <a 
                  href="#quote"
                  onClick={() => setShowQuotePopup(false)}
                  className="w-full bg-orange-700 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-all whitespace-nowrap shadow-lg hover:shadow-xl flex items-center justify-center gap-2 cursor-pointer text-sm sm:text-base"
                >
                  <i className="ri-file-list-3-line"></i>
                  Request GA Site Assessment
                </a>
                <button 
                  onClick={() => setShowQuotePopup(false)}
                  className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section - Redesigned */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20excavator%20and%20bobcat%20machinery%20working%20on%20red%20clay%20soil%20in%20Georgia%20construction%20site%20with%20pine%20trees%20and%20oak%20trees%20in%20background%20showing%20precision%20grading%20equipment%20and%20land%20clearing%20operations%20emphasizing%20Georgia%20terrain%20with%20warm%20natural%20lighting%20and%20professional%20quality%20modern%20construction%20standards&width=1920&height=1080&seq=mtcruzheroGA001&orientation=landscape')`
          }}
        ></div>
        
        <div className="relative z-20 max-w-6xl mx-auto px-4 sm:px-6 text-center text-white">
          <div className="mb-6 opacity-0 animate-slideDown animate-fill-forwards">
            <span className="inline-block bg-orange-700/90 text-white px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold tracking-wide">THE #1 LAND CLEARING & GRADING SPECIALISTS IN GEORGIA</span>
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-6 leading-tight opacity-0 animate-slideUp animate-delay-200 animate-fill-forwards">
            We Master the Georgia<br />
            <span className="text-orange-400">Red Clay</span> so You Can<br />
            Build With Confidence.
          </h1>
          <p className="text-base sm:text-xl md:text-2xl mb-8 text-gray-100 max-w-4xl mx-auto leading-relaxed opacity-0 animate-slideUp animate-delay-400 animate-fill-forwards">
            Serving Greater Atlanta & North Georgia. Premium Land Clearing, Excavation, and Site Prep.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-slideUp animate-delay-600 animate-fill-forwards px-4">
            <a href="#quote" className="bg-orange-700 text-white px-8 sm:px-12 py-5 rounded-xl hover:bg-orange-600 transition-all text-xl sm:text-xl font-bold whitespace-nowrap shadow-2xl hover:shadow-3xl transform hover:scale-110 cursor-pointer">
              Request GA Site Assessment
            </a>
            <a href="tel:+19432385890" className="bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-white/20 transition-all text-base sm:text-lg font-semibold whitespace-nowrap border-2 border-white/30 cursor-pointer flex items-center justify-center gap-2">
              <i className="ri-phone-line"></i>
              <span className="hidden sm:inline">Call Now: </span>(943) 238-5890
            </a>
          </div>
          <p className="text-xs sm:text-sm text-gray-200 mt-6 opacity-0 animate-slideUp animate-delay-800 animate-fill-forwards flex items-center justify-center gap-2">
            <i className="ri-shield-check-line"></i>
            <span>Licensed, Bonded, and Fully Insured for Your Peace of Mind</span>
          </p>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </section>

      {/* Service Area Banner */}
      <section className="py-8 sm:py-10 bg-gradient-to-r from-orange-700 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h3 className="text-white text-lg sm:text-xl md:text-2xl font-bold mb-3">
            Serving High-End Projects in Georgia
          </h3>
          <p className="text-white/90 text-sm sm:text-base">
            <strong>Fulton • Gwinnett • Cobb • Forsyth • Cherokee • Hall Counties</strong>
          </p>
          <p className="text-white/80 text-xs sm:text-sm mt-2">
            Atlanta • Alpharetta • Marietta • Roswell • Johns Creek • Sandy Springs • Cumming • Gainesville
          </p>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center opacity-0 animate-scaleIn animate-fill-forwards">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-orange-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-settings-3-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Dust-Free Equipment</h3>
              <p className="text-gray-300 text-xs sm:text-sm px-4">Advanced machinery minimizing environmental impact</p>
            </div>
            <div className="text-center opacity-0 animate-scaleIn animate-delay-200 animate-fill-forwards">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-orange-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-focus-3-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">GPS Precision Grading</h3>
              <p className="text-gray-300 text-xs sm:text-sm px-4">Laser-accurate leveling for perfect foundations</p>
            </div>
            <div className="text-center opacity-0 animate-scaleIn animate-delay-400 animate-fill-forwards">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-orange-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-cross-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">100% Biblical Integrity</h3>
              <p className="text-gray-300 text-xs sm:text-sm px-4">Christian-owned with unwavering ethical standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us - Red Clay Experts */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-3xl p-8 sm:p-12 md:p-16">
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-orange-700 rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-contrast-drop-2-line text-3xl sm:text-4xl text-white"></i>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  Red Clay Experts
                </h2>
                <p className="text-lg sm:text-xl text-gray-700 leading-relaxed mb-6">
                  <strong className="text-orange-700">Georgia clay is hard.</strong>
                </p>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6">
                  Our heavy machinery is specifically calibrated to grade, level, and drain Georgia's dense red clay soil without future erosion issues. We understand the unique challenges of Atlanta's terrain better than anyone.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-orange-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm sm:text-base">Specialized equipment for red clay compaction</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-orange-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm sm:text-base">Advanced drainage solutions for clay soil</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-orange-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm sm:text-base">Erosion prevention guaranteed</span>
                  </li>
                </ul>
                <a 
                  href="#quote"
                  className="inline-flex items-center justify-center gap-2 bg-orange-700 text-white px-8 py-4 rounded-xl hover:bg-orange-600 transition-all text-lg font-bold whitespace-nowrap shadow-xl hover:shadow-2xl transform hover:scale-105 cursor-pointer"
                >
                  <i className="ri-file-list-3-line"></i>
                  Get Your Red Clay Assessment
                </a>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Close-up%20of%20Georgia%20red%20clay%20soil%20being%20graded%20by%20professional%20excavator%20showing%20dense%20clay%20texture%20and%20professional%20land%20preparation%20work%20with%20MT%20CRUZ%20equipment%20emphasizing%20specialized%20expertise%20in%20challenging%20Georgia%20terrain%20with%20natural%20lighting&width=800&height=1000&seq=redclayGA001&orientation=portrait"
                  alt="Georgia Red Clay Expertise"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-4 sm:p-6 rounded-xl shadow-xl">
                  <p className="text-2xl sm:text-3xl font-bold text-orange-700">15+</p>
                  <p className="text-xs sm:text-sm text-gray-600 font-semibold">Years Mastering<br />Georgia Clay</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Georgia Technical Edge */}
      <section id="technical-edge" className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16 opacity-0 animate-slideUp animate-fill-forwards">
            <span className="text-orange-700 font-semibold text-xs sm:text-sm tracking-wider uppercase">TECHNICAL SUPERIORITY</span>
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mt-4 mb-4 sm:mb-6 px-4">The Georgia Technical Edge</h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Specialized expertise in Georgia's challenging red clay soil and unique terrain conditions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 rounded-2xl border-2 border-gray-100 hover:border-orange-700 transition-all duration-300 hover:shadow-2xl opacity-0 animate-slideUp animate-delay-200 animate-fill-forwards">
              <div className="relative h-48 rounded-xl overflow-hidden mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Advanced%20laser%20leveling%20equipment%20on%20Georgia%20red%20clay%20construction%20site%20with%20pine%20trees%20showing%20precision%20grading%20technology%20with%20GPS%20system%20preventing%20water%20drainage%20issues%20professional%20excavation%20work%20emphasizing%20technical%20excellence%20and%20modern%20construction%20standards%20in%20warm%20natural%20lighting&width=800&height=600&seq=mtcruztechGA001&orientation=landscape"
                  alt="Grading & Drainage"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-orange-700 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-water-flash-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Grading &amp; Drainage</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4">
                Advanced laser leveling to prevent water issues in Georgia's dense clay. Our GPS-guided systems ensure perfect drainage every time.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <i className="ri-check-line text-orange-700 mt-1 flex-shrink-0"></i>
                  <span>Laser-guided precision grading</span>
                </li>
                <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <i className="ri-check-line text-orange-700 mt-1 flex-shrink-0"></i>
                  <span>Red clay drainage solutions</span>
                </li>
                <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <i className="ri-check-line text-orange-700 mt-1 flex-shrink-0"></i>
                  <span>Foundation stability guarantee</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 rounded-2xl border-2 border-gray-100 hover:border-orange-700 transition-all duration-300 hover:shadow-2xl opacity-0 animate-slideUp animate-delay-400 animate-fill-forwards">
              <div className="relative h-48 rounded-xl overflow-hidden mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Forestry%20mulching%20machine%20clearing%20vegetation%20on%20Georgia%20land%20with%20oak%20and%20pine%20trees%20showing%20mulched%20ground%20eco-friendly%20land%20clearing%20process%20preserving%20nutrient-rich%20soil%20and%20preventing%20erosion%20professional%20equipment%20operation%20emphasizing%20environmental%20sustainability%20and%20efficient%20clearing%20methods&width=800&height=600&seq=mtcruztechGA002&orientation=landscape"
                  alt="Forestry Mulching"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-orange-700 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-plant-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Forestry Mulching</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4">
                Efficient, eco-friendly land clearing that leaves your soil nutrient-rich and erosion-resistant. Perfect for Georgia's diverse vegetation.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <i className="ri-check-line text-orange-700 mt-1 flex-shrink-0"></i>
                  <span>Preserves topsoil integrity</span>
                </li>
                <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <i className="ri-check-line text-orange-700 mt-1 flex-shrink-0"></i>
                  <span>Natural erosion control</span>
                </li>
                <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <i className="ri-check-line text-orange-700 mt-1 flex-shrink-0"></i>
                  <span>Environmentally sustainable</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-6 sm:p-8 rounded-2xl border-2 border-gray-100 hover:border-orange-700 transition-all duration-300 hover:shadow-2xl opacity-0 animate-slideUp animate-delay-600 animate-fill-forwards">
              <div className="relative h-48 rounded-xl overflow-hidden mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Emergency%20tree%20removal%20with%20bucket%20truck%20and%20professional%20crew%20responding%20to%20storm%20damage%20on%20Georgia%20property%20with%20pine%20trees%20showing%2024%2F7%20emergency%20service%20rapid%20response%20protecting%20property%20investment%20with%20specialized%20equipment%20and%20safety%20protocols%20in%20action&width=800&height=600&seq=mtcruztechGA003&orientation=landscape"
                  alt="24/7 Storm Response"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-orange-700 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-24-hours-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">24/7 Storm Response</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4">
                Emergency tree and debris removal to protect your property investment. Our bucket truck and crew are ready around the clock.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <i className="ri-check-line text-orange-700 mt-1 flex-shrink-0"></i>
                  <span>Immediate emergency response</span>
                </li>
                <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <i className="ri-check-line text-orange-700 mt-1 flex-shrink-0"></i>
                  <span>Bucket truck available 24/7</span>
                </li>
                <li className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                  <i className="ri-check-line text-orange-700 mt-1 flex-shrink-0"></i>
                  <span>Property protection priority</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 sm:mt-16 bg-gradient-to-r from-orange-700 to-orange-600 rounded-2xl p-8 sm:p-10 text-white text-center opacity-0 animate-slideUp animate-delay-800 animate-fill-forwards">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">Why Georgia Builders Choose MT CRUZ</h3>
            <p className="text-base sm:text-xl mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-4">
              We understand Georgia's unique red clay soil composition, drainage challenges, and building requirements better than anyone. Our specialized equipment and decades of local experience ensure your project starts on solid ground.
            </p>
            <a 
              href="#quote"
              className="inline-flex items-center justify-center gap-2 bg-white text-orange-700 px-8 sm:px-12 py-4 sm:py-5 rounded-xl hover:bg-gray-100 transition-all text-base sm:text-xl font-bold whitespace-nowrap shadow-2xl hover:shadow-3xl transform hover:scale-110 cursor-pointer"
            >
              <i className="ri-file-list-3-line"></i>
              Get Your Free Technical Assessment
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16 opacity-0 animate-slideUp animate-fill-forwards">
            <span className="text-teal-700 font-semibold text-xs sm:text-sm tracking-wider uppercase">Frequently Asked Questions</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-4 sm:mb-6 px-4">Get Your Questions Answered</h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Answers to the most common questions about our land preparation services
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'What areas do you serve?',
                answer: 'We serve exclusively the state of Georgia, focusing on the Greater Atlanta area and North Georgia counties including Fulton, Gwinnett, Cobb, Forsyth, Cherokee, and Hall. We are specialized in the local terrain, red clay soil conditions, and Georgia regulations.'
              },
              {
                question: 'How long does it take to prepare a residential lot?',
                answer: 'The time varies depending on the size and conditions of the land. A typical residential lot of 0.5 to 2.5 acres takes 3 to 7 days. Larger projects or those with special red clay conditions may take longer. We provide a detailed timeline with each quote.'
              },
              {
                question: 'Do you offer emergency services?',
                answer: 'Yes! Our 24/7 emergency service is available throughout Georgia for fallen tree removal, access clearing, and other urgent situations. We have specialized equipment, including a bucket truck, for rapid response.'
              },
              {
                question: 'What is included in the site preparation service?',
                answer: 'Our services include vegetation clearing, laser grading specialized for red clay, soil compaction, utility excavation, erosion control (SWPPP), and complete foundation base preparation. Each project is customized to Georgia building standards and your specific needs.'
              },
              {
                question: 'Do you follow environmental and building regulations?',
                answer: 'Absolutely. We strictly follow all EPA stormwater pollution prevention standards (SWPPP), Georgia state and local building regulations, and engineering specifications. Our team is certified and experienced in Georgia regulatory compliance.'
              },
              {
                question: 'How does the quoting process work?',
                answer: 'After receiving your request, we visit the site to assess the land conditions and red clay characteristics. We then provide a detailed quote within 24-48 hours, including scope of work, timeline, and costs. No surprises or hidden fees.'
              },
              {
                question: 'What equipment do you use?',
                answer: 'We use modern equipment specifically calibrated for Georgia red clay including Bobcat excavators, backhoes, laser grading equipment, forestry mulcher, bucket truck, and specialized compaction machinery. All our equipment is regularly maintained for maximum efficiency.'
              },
              {
                question: 'Do you work on large commercial projects?',
                answer: 'Yes! As a subsidiary of GNX Developer Group, we have the experience and capacity for large-scale commercial projects in Georgia, including subdivisions, commercial developments, and complex infrastructure. We have completed projects with up to 45 lots.'
              },
              {
                question: 'What is the Seed of Love program?',
                answer: 'As a Christian company, we allocate 10% of each contract to infrastructure and education projects in Africa through our Seed of Love foundation. When you choose MT CRUZ, you are helping to build schools and water systems in underserved communities.'
              },
              {
                question: 'Do you offer a warranty on the work performed?',
                answer: 'Yes, we guarantee the quality of all our work. If there are any issues related to our land preparation, we will return to correct them. Our reputation is built on integrity and technical excellence.'
              },
              {
                question: 'Do I need permits before starting?',
                answer: 'Depending on your Georgia municipality and project type, permits may be required. We can guide you on local requirements and, in many cases, assist in the permit acquisition process to ensure full compliance with Georgia regulations.'
              },
              {
                question: 'Why is red clay expertise important?',
                answer: 'Georgia red clay is dense and challenging to work with. Without proper equipment and expertise, it can lead to drainage problems, foundation issues, and erosion. Our specialized machinery and techniques ensure proper compaction, grading, and drainage for long-term stability.'
              }
            ].map((faq, index) => (
              <div 
                key={index}
                className={`bg-gray-50 rounded-xl overflow-hidden border-2 border-gray-100 hover:border-teal-700 transition-all duration-300 opacity-0 animate-slideUp animate-delay-${Math.min(index * 50, 600)} animate-fill-forwards`}
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 pr-4">
                    {faq.question}
                  </h3>
                  <div className={`w-8 h-8 bg-teal-700 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-white text-xl"></i>
                  </div>
                </button>
                
                <div 
                  className={`overflow-hidden transition-all duration-300 ${openFaqIndex === index ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="px-4 sm:px-6 pb-4 sm:pb-5">
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-50 to-teal-100 rounded-2xl p-6 sm:p-8 text-center opacity-0 animate-scaleIn animate-delay-700 animate-fill-forwards">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Still have questions?</h3>
            <p className="text-base sm:text-xl text-gray-700 mb-6 max-w-2xl mx-auto px-4">
              Our Georgia team is ready to answer all your questions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
              <a 
                href="#quote"
                className="inline-flex items-center justify-center gap-2 bg-teal-700 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-xl hover:bg-teal-600 transition-all text-base sm:text-lg font-bold whitespace-nowrap shadow-2xl hover:shadow-3xl transform hover:scale-110"
              >
                <i className="ri-file-list-3-line"></i>
                Request GA Site Assessment
              </a>
              <a 
                href="tel:+19432385890"
                className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-all font-semibold whitespace-nowrap border-2 border-teal-700"
              >
                <i className="ri-phone-line"></i>
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our Higher Standard - Faith & Mission */}
      <section id="mission" className="py-16 sm:py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20construction%20blueprint%20elements%20and%20cross%20symbols%20subtle%20background%20design%20in%20dark%20tones%20emphasizing%20faith%20and%20professionalism%20with%20architectural%20lines%20and%20spiritual%20symbolism%20creating%20elegant%20textured%20backdrop&width=1920&height=1080&seq=mtcruzvalues007&orientation=landscape')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12 opacity-0 animate-slideDown animate-fill-forwards">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-orange-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-heart-line text-3xl sm:text-4xl text-white"></i>
            </div>
            <span className="text-orange-400 font-semibold text-xs sm:text-sm tracking-wider uppercase">OUR HIGHER STANDARD</span>
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mt-4 mb-4 sm:mb-6 px-4">More Than Moving Earth</h2>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 sm:p-10 border border-white/10 mb-12 opacity-0 animate-slideUp animate-delay-300 animate-fill-forwards">
            <p className="text-lg sm:text-2xl md:text-3xl font-light leading-relaxed text-center text-gray-100 mb-6 sm:mb-8">
              As a Christian-owned company, we don't just move earth; we honor a commitment to <span className="text-orange-400 font-semibold">honesty and biblical ethics</span>. Your project with MT CRUZ directly funds the <span className="text-orange-400 font-semibold">Seed of Love Foundation</span>, building infrastructure and hope in Africa.
            </p>
            <div className="flex items-center justify-center gap-3 sm:gap-4 pt-6 border-t border-white/20">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-700 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-cross-line text-xl sm:text-2xl text-white"></i>
              </div>
              <div className="text-left">
                <p className="font-semibold text-base sm:text-lg">Colossians 3:23</p>
                <p className="text-gray-400 text-xs sm:text-sm">"Whatever you do, work at it with all your heart"</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-64 sm:h-80 opacity-0 animate-slideRight animate-delay-500 animate-fill-forwards">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20construction%20equipment%20and%20machinery%20working%20on%20professional%20site%20in%20Georgia%20showing%20advanced%20bobcat%20excavator%20and%20bucket%20truck%20with%20American%20construction%20standards%20high-tech%20operations%20emphasizing%20modern%20development%20and%20technical%20excellence%20with%20pine%20trees&width=800&height=600&seq=mtcruzGA021&orientation=landscape"
                alt="Your Project in Georgia"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
                <h3 className="text-xl sm:text-2xl font-bold mb-2">Your Project in Georgia</h3>
                <p className="text-gray-200 text-xs sm:text-sm">Premium equipment and biblical integrity</p>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-64 sm:h-80 opacity-0 animate-slideLeft animate-delay-500 animate-fill-forwards">
              <img 
                src="https://static.readdy.ai/image/9e98f39e19c16adc0523c7067dfb9e5f/598d290c7dae71d8f6ccf223e7f68f1d.png"
                alt="Seed of Love Foundation"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
                <h3 className="text-xl sm:text-2xl font-bold mb-2">Building Hope in Africa</h3>
                <p className="text-gray-200 text-xs sm:text-sm">Infrastructure through Seed of Love</p>
              </div>
            </div>
          </div>

          <div className="text-center opacity-0 animate-slideUp animate-delay-700 animate-fill-forwards">
            <a 
              href="https://www.instagram.com/seedofloveinc" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl hover:bg-white/20 transition-all border border-white/20 cursor-pointer text-sm sm:text-base"
            >
              <i className="ri-instagram-line text-xl sm:text-2xl"></i>
              <span className="font-semibold">See Our Mission in Africa</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Featured Projects - Georgia Focus */}
      <section id="projects" className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16 opacity-0 animate-slideUp animate-fill-forwards">
            <span className="text-orange-700 font-semibold text-xs sm:text-sm tracking-wider uppercase">PROVEN EXCELLENCE</span>
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mt-4 mb-4 sm:mb-6 px-4">Featured Projects</h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              High-resolution documentation of MT CRUZ equipment mastering Georgia's red clay
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                id: 1,
                title: 'Luxury Home Site - Atlanta Suburbs',
                location: 'Alpharetta, Georgia',
                type: 'Precision Grading',
                image: 'https://readdy.ai/api/search-image?query=MT%20CRUZ%20branded%20bobcat%20excavator%20working%20on%20red%20clay%20soil%20in%20Atlanta%20Georgia%20suburbs%20luxury%20home%20construction%20site%20showing%20precision%20grading%20and%20professional%20land%20preparation%20with%20company%20logo%20visible%20on%20equipment%20emphasizing%20technical%20excellence%20and%20quality%20workmanship%20with%20pine%20trees&width=800&height=600&seq=mtcruzprojectGA001&orientation=landscape',
                description: '3.2 acre luxury estate with laser grading and advanced drainage solutions in Georgia red clay.'
              },
              {
                id: 2,
                title: 'Commercial Development - Marietta',
                location: 'Marietta, Georgia',
                type: 'Large Scale Clearing',
                image: 'https://readdy.ai/api/search-image?query=MT%20CRUZ%20branded%20forestry%20mulcher%20clearing%20large%20commercial%20property%20in%20Marietta%20Georgia%20showing%20efficient%20land%20clearing%20operation%20with%20company%20equipment%20and%20professional%20crew%20working%20on%20extensive%20site%20preparation%20project%20with%20oak%20trees&width=800&height=600&seq=mtcruzprojectGA002&orientation=landscape',
                description: '18 acre commercial site prep with forestry mulching and utility infrastructure.'
              },
              {
                id: 3,
                title: 'Subdivision Infrastructure - Roswell',
                location: 'Roswell, Georgia',
                type: 'Mass Grading',
                image: 'https://readdy.ai/api/search-image?query=MT%20CRUZ%20branded%20excavator%20and%20bobcat%20working%20on%20subdivision%20development%20in%20Roswell%20Georgia%20showing%20mass%20grading%20operation%20with%20multiple%20company%20equipment%20pieces%20visible%20professional%20site%20preparation%20for%20residential%20lots%20with%20pine%20trees&width=800&height=600&seq=mtcruzprojectGA003&orientation=landscape',
                description: '32-lot subdivision with complete infrastructure and GPS-guided precision grading.'
              },
              {
                id: 4,
                title: 'Emergency Storm Response - Athens',
                location: 'Athens, Georgia',
                type: '24/7 Emergency Service',
                image: 'https://readdy.ai/api/search-image?query=MT%20CRUZ%20branded%20bucket%20truck%20responding%20to%20emergency%20tree%20removal%20in%20Athens%20Georgia%20showing%20professional%20storm%20response%20crew%20with%20company%20equipment%20removing%20fallen%20trees%20protecting%20property%20with%20safety%20protocols&width=800&height=600&seq=mtcruzprojectGA004&orientation=landscape',
                description: 'Rapid response fallen tree removal protecting residential property investment.'
              },
              {
                id: 5,
                title: 'Estate Foundation Prep - Johns Creek',
                location: 'Johns Creek, Georgia',
                type: 'Foundation Excellence',
                image: 'https://readdy.ai/api/search-image?query=MT%20CRUZ%20branded%20equipment%20preparing%20luxury%20estate%20foundation%20in%20Johns%20Creek%20Georgia%20showing%20laser%20leveling%20technology%20and%20precision%20compaction%20work%20on%20red%20clay%20with%20company%20machinery%20emphasizing%20technical%20expertise&width=800&height=600&seq=mtcruzprojectGA005&orientation=landscape',
                description: 'Custom estate with engineered foundation base and advanced compaction testing.'
              },
              {
                id: 6,
                title: 'Eco-Friendly Clearing - Cumming',
                location: 'Cumming, Georgia',
                type: 'Forestry Mulching',
                image: 'https://readdy.ai/api/search-image?query=MT%20CRUZ%20branded%20forestry%20mulching%20equipment%20clearing%20land%20in%20Cumming%20Georgia%20showing%20eco-friendly%20land%20clearing%20process%20with%20company%20machinery%20preserving%20soil%20integrity%20and%20preventing%20erosion%20professional%20environmental%20approach%20with%20pine%20trees&width=800&height=600&seq=mtcruzprojectGA006&orientation=landscape',
                description: '12 acres of environmentally sustainable clearing preserving topsoil integrity.'
              }
            ].map((project, index) => (
              <div 
                key={project.id}
                className={`bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer opacity-0 animate-slideUp animate-delay-${Math.min((index + 1) * 100, 800)} animate-fill-forwards`}
                onClick={() => setSelectedProject(project.id)}
              >
                <div className="relative h-48 sm:h-64 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4 bg-orange-700 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {project.type}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1">{project.title}</h3>
                    <div className="flex items-center gap-2 text-white text-xs sm:text-sm">
                      <i className="ri-map-pin-line"></i>
                      <span>{project.location}</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 sm:p-6">
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-4">
                    {project.description}
                  </p>
                  <div className="flex items-center text-orange-700 font-semibold text-xs sm:text-sm group-hover:gap-3 gap-2 transition-all">
                    <span>View Project Details</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 sm:mt-16 text-center opacity-0 animate-slideUp animate-delay-900 animate-fill-forwards">
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6 sm:p-10">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">See Your Georgia Project Come to Life</h3>
              <p className="text-base sm:text-xl text-gray-700 mb-6 max-w-2xl mx-auto px-4">
                Every MT CRUZ project is documented with high-resolution photos showing our equipment and expertise in action across Georgia.
              </p>
              <a 
                href="#quote"
                className="inline-flex items-center justify-center gap-2 bg-orange-700 text-white px-8 sm:px-12 py-4 sm:py-5 rounded-xl hover:bg-orange-600 transition-all text-base sm:text-xl font-bold whitespace-nowrap shadow-2xl hover:shadow-3xl transform hover:scale-110"
              >
                <i className="ri-file-list-3-line"></i>
                Start Your Georgia Project Today
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* GNX Connection - Corporate Authority */}
      <section id="gnx" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-12 sm:gap-16 items-center">
            <div className="opacity-0 animate-slideRight animate-fill-forwards">
              <span className="text-orange-700 font-semibold text-xs sm:text-sm tracking-wider uppercase">CORPORATE STRENGTH, LOCAL SERVICE</span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-4 sm:mb-6 px-4">A GNX Developer Group Company</h2>
              <p className="text-base sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                MT CRUZ is a specialized infrastructure division of GNX Developer Group, bringing international construction standards with the agility of local Georgia expertise.
              </p>
              <div className="space-y-4 mb-6 sm:mb-8">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-8 h-8 bg-orange-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-global-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">International Standards</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">Global construction expertise applied to Georgia projects</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-8 h-8 bg-orange-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-team-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Local Georgia Agility</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">Responsive service with deep understanding of Georgia terrain and regulations</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-8 h-8 bg-orange-700 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-building-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Full-Service Development</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">From site prep to complete construction through our parent company</p>
                  </div>
                </div>
              </div>
              <a 
                href="https://www.gnxconstruction.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-orange-700 font-semibold hover:text-orange-600 transition-colors text-base sm:text-lg cursor-pointer"
              >
                Visit www.gnxconstruction.com
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="relative opacity-0 animate-slideLeft animate-delay-300 animate-fill-forwards">
              <a 
                href="https://www.gnxconstruction.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block group cursor-pointer"
              >
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 sm:p-16 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 group-hover:border-orange-700">
                  <div className="space-y-3">
                    <div className="bg-white rounded-xl p-4 sm:p-5 shadow-md group-hover:shadow-lg transition-shadow border border-gray-100">
                      <p className="text-gray-800 font-semibold text-base sm:text-lg">International Development Standards</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 sm:p-5 shadow-md group-hover:shadow-lg transition-shadow border border-gray-100">
                      <p className="text-gray-800 font-semibold text-base sm:text-lg">Decades of Combined Experience</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 sm:p-5 shadow-md group-hover:shadow-lg transition-shadow border border-gray-100">
                      <p className="text-gray-800 font-semibold text-base sm:text-lg">Residential &amp; Commercial Excellence</p>
                    </div>
                  </div>
                </div>
              </a>
              <div className="absolute -bottom-8 -right-8 w-32 h-32 sm:w-40 sm:h-40 bg-orange-700 rounded-2xl -z-10 opacity-20"></div>
              <div className="absolute -top-8 -left-8 w-32 h-32 sm:w-40 sm:h-40 bg-slate-900 rounded-2xl -z-10 opacity-20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Lead Gen Form */}
      <section id="quote" className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 opacity-0 animate-slideUp animate-fill-forwards">
            <span className="text-orange-700 font-semibold text-xs sm:text-sm tracking-wider uppercase">GET STARTED TODAY</span>
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mt-4 mb-4 sm:mb-6 px-4">Request Your Free Georgia Site Assessment</h2>
            <p className="text-base sm:text-xl text-gray-600 px-4">
              Detailed technical assessment and transparent pricing within 3 hours
            </p>
          </div>

          <form 
            id="lead-magnet-form" 
            data-readdy-form 
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-2xl opacity-0 animate-slideUp animate-delay-300 animate-fill-forwards"
          >
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:border-orange-600 focus:ring-2 focus:ring-orange-600/20 outline-none transition-all text-xs sm:text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:border-orange-600 focus:ring-2 focus:ring-orange-600/20 outline-none transition-all text-xs sm:text-sm"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label htmlFor="phone" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:border-orange-600 focus:ring-2 focus:ring-orange-600/20 outline-none transition-all text-xs sm:text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="projectType" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Project Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:border-orange-600 focus:ring-2 focus:ring-orange-600/20 outline-none transition-all text-xs sm:text-sm"
                >
                  <option value="">Select project type</option>
                  <option value="Residential Land Prep">Residential Land Prep</option>
                  <option value="Commercial Development">Commercial Development</option>
                  <option value="Forestry Mulching">Forestry Mulching</option>
                  <option value="Emergency Service">Emergency Service</option>
                  <option value="Subdivision Infrastructure">Subdivision Infrastructure</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div>
                <label htmlFor="zipCode" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Project Zip Code (Georgia) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:border-orange-600 focus:ring-2 focus:ring-orange-600/20 outline-none transition-all text-xs sm:text-sm"
                  placeholder="30301 (Atlanta, GA)"
                />
              </div>

              <div>
                <label htmlFor="timeline" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Timeline <span className="text-red-500">*</span>
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  required
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:border-orange-600 focus:ring-2 focus:ring-orange-600/20 outline-none transition-all text-xs sm:text-sm"
                >
                  <option value="">Select timeline</option>
                  <option value="Ready to start">Ready to start</option>
                  <option value="Planning (1-3 months)">Planning (1-3 months)</option>
                  <option value="Future (3+ months)">Future (3+ months)</option>
                </select>
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <label htmlFor="message" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Project Details
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                maxLength={500}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border border-gray-300 focus:border-orange-600 focus:ring-2 focus:ring-orange-600/20 outline-none transition-all resize-none text-xs sm:text-sm"
                placeholder="Tell us about your Georgia land preparation needs... (Max 500 characters)"
              ></textarea>
              <div className="text-right text-xs sm:text-sm text-gray-500 mt-1">
                {formData.message.length}/500 characters
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-xs sm:text-sm flex items-start gap-2">
                <i className="ri-check-line flex-shrink-0 mt-0.5"></i>
                <span>Thank you! We'll contact you within 3 hours with your detailed Georgia site assessment.</span>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm flex items-start gap-2">
                <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
                <span>Something went wrong. Please call us directly at (943) 238-5890.</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-orange-700 text-white py-3 sm:py-5 rounded-xl font-bold text-base sm:text-xl hover:bg-orange-600 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap shadow-2xl hover:shadow-3xl transform hover:scale-105 cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Sending...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-send-plane-line"></i>
                  Get Free Georgia Site Assessment
                </span>
              )}
            </button>

            <div className="mt-4 sm:mt-6 flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-orange-700"></i>
                <span>Licensed &amp; Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-orange-700"></i>
                <span>3-Hour Response</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-lock-line text-orange-700"></i>
                <span>Privacy Protected</span>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div className="md:col-span-2">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20construction%20company%20logo%20MT%20CRUZ%20with%20modern%20design%20featuring%20construction%20equipment%20silhouettes%20and%20bold%20typography%20emphasizing%20reliability%20and%20technical%20excellence%20in%20warm%20earth%20tones%20and%20professional%20colors&width=400&height=200&seq=mtcruzlogo004&orientation=landscape" 
                alt="MT CRUZ Logo" 
                className="h-16 sm:h-20 w-auto object-contain mb-4 sm:mb-6"
              />
              <p className="text-gray-300 mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base">
                Premier land clearing, grading, and excavation services in Georgia. Specialized in red clay terrain. Driven by biblical principles and technical excellence.
              </p>
              <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6 italic">
                MT CRUZ is a specialized infrastructure division of GNX Developer Group
              </p>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-cross-line text-lg sm:text-xl"></i>
                <span className="font-semibold text-sm sm:text-base">Christian Owned &amp; Operated</span>
              </div>
              <div className="flex gap-3 sm:gap-4">
                <a href="https://www.instagram.com/seedofloveinc" target="_blank" rel="noopener noreferrer" className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-lg sm:text-xl"></i>
                </a>
                <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-facebook-line text-lg sm:text-xl"></i>
                </a>
                <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-linkedin-line text-lg sm:text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#technical-edge" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">Technical Edge</a></li>
                <li><a href="#projects" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">Featured Projects</a></li>
                <li><a href="#mission" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">Our Mission</a></li>
                <li><a href="#gnx" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">About GNX</a></li>
                <li><a href="#quote" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">Get Assessment</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Contact Info</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-lg sm:text-xl mt-1 flex-shrink-0"></i>
                  <div className="text-gray-300 text-sm sm:text-base">
                    <p className="font-semibold mb-1">Proudly Serving Georgia</p>
                    <p className="text-xs sm:text-sm">Atlanta • Alpharetta • Marietta<br />Roswell • Johns Creek • Cumming<br />and surrounding counties</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-phone-line text-lg sm:text-xl mt-1 flex-shrink-0"></i>
                  <a href="tel:+19432385890" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    (943) 238-5890
                  </a>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-mail-line text-lg sm:text-xl mt-1 flex-shrink-0"></i>
                  <a href="mailto:info@mtcruz.com" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm sm:text-base">
                    info@mtcruz.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6 sm:pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-300 text-xs sm:text-sm text-center md:text-left">
                © 2024 MT CRUZ. All rights reserved. | A GNX Developer Group Company
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
                <a 
                  href="https://www.gnxconstruction.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors text-xs sm:text-sm cursor-pointer"
                >
                  www.gnxconstruction.com
                </a>
                <a 
                  href="https://readdy.ai/?ref=logo" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors text-xs sm:text-sm cursor-pointer"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
